import { useEffect } from 'react';
import { DeviceOSType } from '@/api/user-portal-api-generated';
import { getMobilePlatform } from '@/features/onboarding/utils/mobile-platform';
import {
  getAppStoreUrl,
  getInjectedIosAppStoreId,
  getPlayStoreUrl,
} from '@/utils/mobile-app-store';

const APP_MISSING_PARAM = 'app';
const APP_MISSING_VALUE = 'missing';

interface UseMobileInstallAppProps {
  deepLinkUrl: string;
  playStoreReferrer?: string;
}

export function useMobileInstallApp({
  deepLinkUrl,
  playStoreReferrer,
}: UseMobileInstallAppProps) {
  const mobilePlatform = getMobilePlatform();
  const playStoreUrl = getPlayStoreUrl(playStoreReferrer);
  const appStoreUrl = getAppStoreUrl();

  const showPlayStore =
    mobilePlatform === DeviceOSType.ANDROID || mobilePlatform === null;
  const showAppStore =
    mobilePlatform === DeviceOSType.IOS || mobilePlatform === null;
  const isAppMissing =
    new URLSearchParams(window.location.search).get(APP_MISSING_PARAM) ===
    APP_MISSING_VALUE;

  useIosSmartAppBanner({
    mobilePlatform,
    iosAppStoreId: getInjectedIosAppStoreId(),
    deepLinkUrl,
  });

  const handleOpenApp = () => {
    window.location.href =
      mobilePlatform === DeviceOSType.ANDROID
        ? toAndroidIntentUrl(deepLinkUrl, buildAppMissingUrl())
        : deepLinkUrl;
  };

  return {
    playStoreUrl,
    appStoreUrl,
    showPlayStore,
    showAppStore,
    isAppMissing,
    handleOpenApp,
  };
}

function buildAppMissingUrl() {
  const url = new URL(window.location.href);
  url.searchParams.set(APP_MISSING_PARAM, APP_MISSING_VALUE);

  return url.toString();
}

// Chrome drops an unhandled custom scheme silently; an intent falls back
function toAndroidIntentUrl(deepLinkUrl: string, fallbackUrl: string) {
  const [scheme, target] = deepLinkUrl.split('://');

  return `intent://${target}#Intent;scheme=${scheme};S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;
}

interface UseIosSmartAppBannerProps {
  mobilePlatform: DeviceOSType | null;
  iosAppStoreId: string | null;
  deepLinkUrl: string;
}

function useIosSmartAppBanner({
  mobilePlatform,
  iosAppStoreId,
  deepLinkUrl,
}: UseIosSmartAppBannerProps) {
  useEffect(() => {
    if (mobilePlatform !== DeviceOSType.IOS || !iosAppStoreId) {
      return;
    }

    const meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    meta.content = `app-id=${iosAppStoreId}, app-argument=${deepLinkUrl}`;
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, [mobilePlatform, iosAppStoreId, deepLinkUrl]);
}
